<template>
  <div class="interaction-editor-wrapper">
    <vue2-tinymce-editor
      v-model="content"
      :options="options"
      v-if="!loadingMce"
      spellcheck="true"
      ref="editorContainer"
    ></vue2-tinymce-editor>

    <MentionPicker
      :visible="showUserSelector"
      :x="selectorX"
      :y="selectorY"
      :placement="selectorPlacement"
      :listMaxHeight="selectorMaxHeight"
      :query="mentionQuery"
      :projectId="workingProjectId"
      :types="['users','tasks','rdis','additionals','entregas']"
      @select="insertMentionFromPicker"
      @close="closeMentionPicker"
    />
  </div>
</template>

<script>
import { Vue2TinymceEditor } from 'vue2-tinymce-editor'
import MentionPicker from '@/components/Globals/Helpers/MentionPicker'

function removeInlineBase64Images(html) {
  if (!html) return ''

  const doc = new DOMParser().parseFromString(html, 'text/html')
  doc.querySelectorAll('img[src]').forEach(image => {
    const src = (image.getAttribute('src') || '').trim().toLowerCase()
    if (src.startsWith('data:image/')) image.remove()
  })
  return doc.body.innerHTML
}

export default {
  name: 'InteractionEditor',
  components: { Vue2TinymceEditor, MentionPicker },
  props: {
    value: { type: String, default: '' },
    editor_options: { type: Object, default: () => ({}) },
    item: { type: Object, default: null },
		item_type: { type: String, default: null },
		allowInlineImages: { type: Boolean, default: true }
  },
  computed: {
    selectedProject() { return this.$store.projectsStore.state.selectedProject },
		workingProjectId() {
			if(this.item?.projectId || this.item?.project_id) return this.item.projectId || this.item.project_id
			return this.selectedProject ? this.selectedProject.id : null
		},
    company_users() { return this.$store.usersStore.state.usersListForItems.users }
  },
  data() {
    return {
      loadingMce: true,
      content: this.allowInlineImages ? (this.value || '') : removeInlineBase64Images(this.value || ''),
      options: {
        plugins: 'autoresize link table lists',
        min_height: 200,
        max_height: 400,
        autoresize_bottom_margin: 8,
        menubar: false,
        branding: false,
        browser_spellcheck: true,
        contextmenu: false,
        toolbar1: 'fontsizeselect | bold italic underline strikethrough forecolor backcolor | alignleft aligncenter alignright alignjustify | numlist bullist outdent indent | link table',
        language: 'es',
        language_url: 'https://cdn.tiny.cloud/1/no-api-key/tinymce/5/langs/es.js',
        content_style: `
          p { margin: 0!important; }
          .user-mention {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            background-color: #e3f2fd;
            color: #1565c0;
            border-radius: 12px;
            padding: 2px 8px 2px 6px;
            font-size: 0.875em;
            font-weight: 500;
            cursor: default;
            user-select: none;
            white-space: nowrap;
            vertical-align: middle;
          }
          .mention-chip {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            background-color: #EEF2FC;
            color: #2D4B95;
            border-radius: 12px;
            padding: 2px 8px 2px 6px;
            font-size: 0.875em;
            font-weight: 500;
            cursor: default;
            user-select: none;
            white-space: nowrap;
            vertical-align: middle;
          }
          .mention-remove {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: 14px;
            height: 14px;
            border-radius: 50%;
            background-color: #5E85EF;
            color: white;
            cursor: pointer;
            font-size: 11px;
            line-height: 1;
            font-style: normal;
          }
          .mention-remove:hover { background-color: #2D4B95; }
					.assoc-btn{
						display: inline-flex;
						align-items: center;
						gap: 4px;
						background-color: #f0f4ff;
						color: #2D4B95;
						border-radius: 12px;
						padding: 4px 12px;
						font-size: 0.875em;
						font-weight: 500;
						cursor: pointer;
						transition: background-color 0.15s, transform 0.1s;
					}

					.assoc-btn:focus {
						outline: none;
						box-shadow: 0 0 0 3px rgba(93,133,239,0.14);
					}
        `,
        setup: this.initTinyMce,
        ...(this.editor_options || {})
      },
      // Menciones
      showUserSelector: false,
      selectorX: 0,
      selectorY: 0,
      selectorPlacement: 'below',
      selectorMaxHeight: 300,
      mentionQuery: '',
      editorInstance: null
    }
  },
  watch: {
    value(newValue) {
      const sanitizedValue = this.sanitizeContent(newValue)
      if (sanitizedValue !== this.content) this.content = sanitizedValue
    },
    content(newValue) {
      const sanitizedContent = this.sanitizeContent(newValue)
      if (sanitizedContent !== newValue) {
        this.content = sanitizedContent
        return
      }
      this.$emit('input', newValue)
      this.$emit('mentions-updated', this.extractMentionedUsers(newValue))
      this.$emit('mentions-structured', this.extractMentionedStructured(newValue))
    }
  },
  mounted() {
    this.$nextTick(() => {
			console.log('InteractionEditor mounted, loadingMce:', this.loadingMce)
      this.loadingMce = false
      if (!this.company_users || this.company_users.length === 0) this.getUserListforItems()
    })
  },
  methods: {
    sanitizeContent(html) {
      return this.allowInlineImages ? (html || '') : removeInlineBase64Images(html)
    },

    getUserListforItems() {
      const project_id = this.selectedProject?.id
        || this.$store.consultasStore.state.currentTarea?.project_id
        || this.$store.rdiStore.state.currentRDI?.project_id
        || this.$store.adicionalesStore.state.currentAdicional?.project_id
      this.$store.usersStore.dispatch('getUsersListForItems', { project_id, start: 0, limit: 9999 })
    },

    extractMentionedUsers(html) {
      if (!html) return []
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const ids = []
      doc.querySelectorAll('.user-mention[data-user-id]').forEach(chip => {
        const id = parseInt(chip.getAttribute('data-user-id'), 10)
        if (id && !ids.includes(id)) ids.push(id)
      })
      return ids
    },

    extractMentionedStructured(html) {
      if (!html) return []
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const items = []
      doc.querySelectorAll('.user-mention[data-user-id]').forEach(chip => {
        const id = parseInt(chip.getAttribute('data-user-id'), 10)
        if (id) items.push({ type: 'user', id })
      })
      doc.querySelectorAll('.assoc-btn[data-id][data-type]').forEach(chip => {
        const id = parseInt(chip.getAttribute('data-id'), 10)
        const type = chip.getAttribute('data-type')
        if (id && type) items.push({ type, id })
      })
      return items
    },

    initTinyMce(editor) {
      this.editorInstance = editor

      editor.on('paste', (e) => {
        const clipboard = e.clipboardData || e.originalEvent.clipboardData
        if (!clipboard) return
        const imageItem = Array.from(clipboard.items || []).find(i => i.kind === 'file' && i.type.startsWith('image'))
        if (imageItem) {
          e.preventDefault()
          const file = imageItem.getAsFile()
          if (file) this.$emit('paste-image', file)
          return
        }
        const pastedHtml = clipboard.getData?.('text/html')
        if (!this.allowInlineImages && pastedHtml && pastedHtml !== this.sanitizeContent(pastedHtml)) {
          e.preventDefault()
          editor.insertContent(this.sanitizeContent(pastedHtml))
          return
        }
        const pastedText = clipboard.getData?.('Text')
        if (pastedText) this.$emit('paste-text', pastedText)
      })

      editor.on('click', (e) => {
        if (e.target?.classList?.contains('mention-remove')) {
          const chip = e.target.closest?.('.user-mention, .assoc-btn') || e.target.parentElement
          if (chip?.parentNode) {
            chip.parentNode.removeChild(chip)
            const newContent = editor.getContent()
            this.content = newContent
            this.$emit('mentions-updated', this.extractMentionedUsers(newContent))
            this.$emit('mentions-structured', this.extractMentionedStructured(newContent))
          }
        }
      })

      editor.on('keyup', (e) => {
        if (e.key === 'Escape') {
          this.closeMentionPicker()
          return
        }
        this.updateMentionState(editor)
      })

      // keydown captura flechas y Enter para delegarlos al picker
      editor.on('keydown', (e) => {
        if (!this.showUserSelector) return
        if (['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
          e.preventDefault()
          e.stopPropagation()
          // Re-emitir el evento al picker a través de una ref o con un bus
          // Lo manejamos emitiendo al picker vía ref
          this.$nextTick(() => {
            const picker = this.$refs.mentionPicker
            if (!picker) return
            if (e.key === 'ArrowDown') picker.highlightNext()
            else if (e.key === 'ArrowUp') picker.highlightPrev()
            else if (e.key === 'Enter') picker.selectHighlighted()
          })
        }
      })
    },

    updateMentionState(editor) {
      const node = editor.selection.getNode()
      const text = node.textContent || ''
      const lastAtIndex = text.lastIndexOf('@')

      if (lastAtIndex === -1) {
        if (this.showUserSelector) this.closeMentionPicker()
        return
      }

      const textAfterAt = text.substring(lastAtIndex + 1)

      // Cerrar si hay un espacio luego del @ (indica que la mención fue abandonada)
      if (textAfterAt.includes(' ')) {
        if (this.showUserSelector) this.closeMentionPicker()
        return
      }

      // Actualizar query y posición
      this.mentionQuery = textAfterAt
      this.positionPicker(editor)
      this.showUserSelector = true
    },

    positionPicker(editor) {
      try {
        const iframeEl = editor.getContainer().querySelector('iframe')
        const iframeRect = iframeEl.getBoundingClientRect()
        const iframeSel = iframeEl.contentWindow.getSelection()

        // Coordenadas absolutas de viewport (para position: fixed)
        let cursorLeft   = iframeRect.left
        let cursorTop    = iframeRect.top
        let cursorBottom = iframeRect.bottom

        if (iframeSel?.rangeCount > 0) {
          const range = iframeSel.getRangeAt(0)
          let rect = range.getBoundingClientRect()
          if (!rect || (rect.width === 0 && rect.height === 0)) {
            const node = range.startContainer
            const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node
            if (el) rect = el.getBoundingClientRect()
          }
          // rect es relativo al iframe; sumar el offset del iframe en el viewport
          cursorLeft   = rect.left   + iframeRect.left
          cursorTop    = rect.top    + iframeRect.top
          cursorBottom = rect.bottom + iframeRect.top
        }

        const PICKER_MARGIN = 6
        const SAFE_MARGIN   = 12
        const PICKER_CHROME = 80
        const MIN_LIST_H    = 120
        const viewportH     = window.innerHeight

        const spaceBelow = viewportH  - cursorBottom - PICKER_MARGIN - SAFE_MARGIN
        const spaceAbove = cursorTop  - PICKER_MARGIN - SAFE_MARGIN

        if (spaceBelow >= MIN_LIST_H + PICKER_CHROME) {
          // Cabe debajo: anclar borde superior del picker al cursor (top fijo)
          this.selectorPlacement = 'below'
          this.selectorY         = cursorBottom + PICKER_MARGIN
          this.selectorMaxHeight = spaceBelow - PICKER_CHROME
        } else if (spaceAbove >= MIN_LIST_H + PICKER_CHROME) {
          // No cabe abajo, pero cabe arriba: anclar borde inferior del picker al cursor (bottom fijo)
          this.selectorPlacement = 'above'
          // y = distancia desde el borde inferior del viewport hasta el cursor
          this.selectorY         = viewportH - cursorTop + PICKER_MARGIN
          this.selectorMaxHeight = spaceAbove - PICKER_CHROME
        } else {
          // Poco espacio en ambos lados: elegir el mayor
          if (spaceBelow >= spaceAbove) {
            this.selectorPlacement = 'below'
            this.selectorY         = cursorBottom + PICKER_MARGIN
            this.selectorMaxHeight = Math.max(MIN_LIST_H, spaceBelow - PICKER_CHROME)
          } else {
            this.selectorPlacement = 'above'
            this.selectorY         = viewportH - cursorTop + PICKER_MARGIN
            this.selectorMaxHeight = Math.max(MIN_LIST_H, spaceAbove - PICKER_CHROME)
          }
        }

        this.selectorX = Math.max(0, cursorLeft)
      } catch (err) {
        console.error('Error al posicionar MentionPicker:', err)
        this.selectorX = 0
        this.selectorY = 0
      }
    },

    insertMentionFromPicker(item) {
      if (!this.editorInstance) return
      const editor = this.editorInstance
      const iframeWin = editor.getWin()
      const iframeSel = iframeWin.getSelection()

      if (!iframeSel?.rangeCount) { this.closeMentionPicker(); return }

      const cursorRange = iframeSel.getRangeAt(0)
      const startContainer = cursorRange.startContainer
      const textNode = startContainer.nodeType === Node.TEXT_NODE
        ? startContainer
        : startContainer.firstChild

      if (!textNode || textNode.nodeType !== Node.TEXT_NODE) { this.closeMentionPicker(); return }

      const nodeText = textNode.textContent || ''
      const lastAtIndex = nodeText.lastIndexOf('@')
      if (lastAtIndex === -1) { this.closeMentionPicker(); return }

      // Seleccionar desde el @ hasta el cursor para reemplazar
      const mentionRange = editor.getDoc().createRange()
      mentionRange.setStart(textNode, lastAtIndex)
      mentionRange.setEnd(textNode, cursorRange.startOffset)
      editor.selection.setRng(mentionRange)

      const safeLabel = (item.label || String(item.id || ''))
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')

      const chipHtml = item.type === 'user'
        ? `<span class="user-mention" contenteditable="false" data-user-id="${item.id}" data-user-name="${safeLabel}">@${safeLabel}<i class="mention-remove" title="Eliminar mención">×</i></span>&nbsp;`
        : `<span class="assoc-btn" contenteditable="false" role="button" tabindex="0" title="Abrir: ${safeLabel}" aria-label="Abrir" data-id="${item.id}" data-type="${item.type}">${safeLabel}</span>&nbsp;`

      editor.insertContent(chipHtml)
      this.closeMentionPicker()
    },

    closeMentionPicker() {
      this.showUserSelector = false
      this.mentionQuery = ''
    }
  }
}
</script>

<style scoped>
.interaction-editor-wrapper {
  position: relative;
  display: inline-block;
  width: 100%;
}
</style>